/**
 * Pure aggregation for the fleet screen's summary header (orchestrator
 * course correction, 2026-09-28: "Add a fleet summary header (organisms
 * alive/stalled count, combined NAV, combined day P&L)"). Everything here
 * reads only `fleet.json`'s own published fields (`FleetOrganismEntry`) —
 * the per-organism day P&L is computed elsewhere (each organism's own
 * `equityCurve()`, `vitals.ts`'s `computeDayPnl`) and passed in once it
 * resolves; this module never fetches anything itself.
 */

import type { FleetOrganismEntry } from "../types/incremental";
import { fleetStatus } from "./fleetStatus";

export interface FleetCounts {
  readonly alive: number;
  readonly stalled: number;
  /** OQA: units reported stopped (incl. a stopped fresh episode). */
  readonly stopped: number;
  readonly notStarted: number;
  readonly total: number;
}

/** Counts organisms by the same heartbeat-derived freshness
 * `FleetScreen.ts`'s cards already show per-row — "alive" means a recent
 * heartbeat, "stalled" means started with a heartbeat that's gone quiet,
 * "not started" covers both a truly unstarted organism and a started one
 * with no heartbeat at all yet (both read as "not yet contributing",
 * distinct from a real stall). */
export function fleetCounts(organisms: readonly FleetOrganismEntry[], now: number): FleetCounts {
  let alive = 0;
  let stalled = 0;
  let stopped = 0;
  let notStarted = 0;
  for (const organism of organisms) {
    const kind = fleetStatus(organism, now).kind;
    if (kind === "active") alive += 1;
    else if (kind === "stalled" || kind === "restarting") stalled += 1;
    else if (kind === "stopped" || kind === "pending" || kind === "failed") stopped += 1;
    else notStarted += 1;
  }
  return { alive, stalled, stopped, notStarted, total: organisms.length };
}

function parseNav(value: string | null): number | null {
  if (value === null) return null;
  const n = Number.parseFloat(value);
  return Number.isFinite(n) ? n : null;
}

/** Sums every organism's own published NAV — `null` only when *none* of
 * them have one yet (never a fabricated $0 for an unstarted fleet). An
 * organism with no NAV yet is simply excluded from the sum, not treated as
 * zero capital. */
export function combinedNav(organisms: readonly FleetOrganismEntry[]): number | null {
  const values = organisms.map((o) => parseNav(o.nav)).filter((n): n is number => n !== null);
  if (values.length === 0) return null;
  return values.reduce((sum, n) => sum + n, 0);
}

/** Sums whichever per-organism day-P&L figures are already known (`Map`
 * keyed by `role`, `null` = not yet resolved/unavailable) — a partial sum
 * while some organisms' figures are still loading is real and useful
 * (labelled "so far" by the caller), never blocked on every fetch
 * finishing. `null` only when *no* organism's figure is known yet. */
export function combinedDayPnl(dayPnlByRole: ReadonlyMap<string, number | null>): number | null {
  const known = Array.from(dayPnlByRole.values()).filter((n): n is number => n !== null);
  if (known.length === 0) return null;
  return known.reduce((sum, n) => sum + n, 0);
}
